// The open section's tabs, drawn in the sidebar (Marius, side navigation option B, 4 Nov 2026).
// Each area still works out its own tabs (who may see Budget, whether Approvals shows, counts);
// it hands them to the shell with useSectionTabs instead of drawing a tab row in the page.
// The shell remembers the last tabs of every section, so the sidebar can unfold a section at once
// when you come back to it, while the section you leave folds shut (Edgar, 5 Nov 2026: the
// sections "move with each other").
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { isCurrentTab, type TabItem } from "../ui/Nav";
import type { NavSection } from "./Header";

export type SectionTab = { key: string; to: string; label: ReactNode; current: boolean };
export type Section = Exclude<NavSection, null>;
/** `label` names the tab list for a screen reader ("Contract & finance"); `pathname`: where it was handed over. */
export type Published = { section: Section; label: string; tabs: SectionTab[]; pathname: string };
type Ctx = { published: Published | null; known: Partial<Record<Section, Published>>; publish: (p: Published) => void };

const SectionTabsContext = createContext<Ctx | null>(null);

export function SectionTabsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ published: Published | null; known: Partial<Record<Section, Published>> }>({ published: null, known: {} });
  // A steady function: pages publish from an effect that lists it, so a new one each time would loop.
  const publish = useCallback((p: Published) => setState((s) => ({ published: p, known: { ...s.known, [p.section]: p } })), []);
  const value = useMemo<Ctx>(() => ({ ...state, publish }), [state, publish]);
  return <SectionTabsContext.Provider value={value}>{children}</SectionTabsContext.Provider>;
}

/** The last tabs every section handed over, by section. */
export function useKnownSectionTabs(): Partial<Record<Section, Published>> {
  return useContext(SectionTabsContext)?.known ?? {};
}

/**
 * A section's tabs as a list of links. Which one is current: what the page said, when it is the
 * page on screen; otherwise (the sidebar unfolding a section before its page has loaded) the tab
 * whose address the current address starts with.
 */
export function SectionTabList({ tabs, label, hidden = false }: { tabs: Published; label?: string; hidden?: boolean }) {
  const { pathname } = useLocation();
  const fresh = tabs.pathname === pathname;
  const guess = fresh ? null : guessCurrent(tabs.tabs, pathname);
  return (
    <nav className="sidebar__tabs" aria-label={label ?? tabs.label} aria-hidden={hidden || undefined}>
      {tabs.tabs.map((t) => {
        const current = fresh ? t.current : t.key === guess;
        return (
          <Link key={t.key} className="sidebar__tab" to={t.to} tabIndex={hidden ? -1 : undefined} aria-current={current ? "page" : undefined}>
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

function guessCurrent(tabs: SectionTab[], pathname: string): string | undefined {
  const hits = tabs.filter((t) => {
    const path = t.to.split("?")[0];
    return pathname === path || pathname.startsWith(`${path}/`);
  });
  return hits.sort((a, b) => b.to.length - a.to.length)[0]?.key;
}

/**
 * The tabs the page on screen handed over, drawn as the sidebar draws them: for a test that renders
 * one page with its section's tabs above it (test/render.tsx).
 */
export function SectionTabsNav() {
  const published = useContext(SectionTabsContext)?.published ?? null;
  if (!published || !published.tabs.length) return null;
  return <SectionTabList tabs={published} />;
}

/**
 * A page of a section hands its tabs to the sidebar. Outside the app shell (a test that renders
 * one page without the harness's provider) there is no sidebar and nothing happens.
 */
export function useSectionTabs(section: Section, label: string, tabs: SectionTab[]): void {
  const ctx = useContext(SectionTabsContext);
  const publish = ctx?.publish;
  const { pathname } = useLocation();
  // The tabs as text, so a new array with the same tabs does not publish again.
  const key = JSON.stringify(tabs.map((t) => [t.key, t.to, typeof t.label === "string" ? t.label : t.key, t.current]));
  useEffect(() => {
    publish?.({ section, label, tabs, pathname });
    // `tabs` is read through `key`: the same tabs publish once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, section, label, key, pathname]);
}

/** The same, from the tab list of ui/Tabs: the current tab is worked out from the address. */
export function useSectionTabItems(section: Section, label: string, items: TabItem[]): void {
  const { pathname, search } = useLocation();
  useSectionTabs(
    section,
    label,
    items.map((it) => ({ key: it.key ?? it.to, to: it.to, label: it.label, current: isCurrentTab(it, pathname, search, items) })),
  );
}
